import {
  COVERAGE_SECTION_TITLE,
  FOCUS_SECTION_TITLE,
  MAX_INLINE_LIST_ITEMS,
  MAX_SECTION_ITEMS,
  MAX_SIGNAL_ITEMS,
  NO_OBVIOUS_DIG_ITEM,
  OPERATIONAL_CONTEXT_SECTION_TITLE,
  WHAT_CHANGED_SECTION_TITLE,
  WORTH_DIGGING_SECTION_TITLE,
} from "./runsSummaryConstants"
import { buildCoverageComparison } from "./runsSummaryCoverage"
import {
  buildCoverageConcentrationDigItem,
  buildCoverageDriverItem,
  buildFailureClusterItem,
  buildNewCoverageDigItem,
  buildNewCoverageItem,
} from "./runsSummaryCoverageDetail"
import {
  buildMetricWindowStats,
  countEntities,
  countMetrics,
  formatCountEntries,
  formatOperationalMetricList,
  getRowNoun,
  getRowsForWindow,
  getRowsWithMetricValue,
  getTopEntries,
  isOperationalContextMetric,
  isUnsuccessfulRow,
  metricMatchesFocus,
} from "./runsSummaryDetailData"
import { formatCount, formatMetricNumber, pluralize } from "./runsSummaryFormat"
import {
  buildFocusItems,
  buildMetricConcernItem,
  buildMetricMovementItem,
  getMetricDeltaMagnitude,
  isPriorityMetric,
  isSignificantMetricMovement,
} from "./runsSummaryMetricDetail"
import type {
  DetailSummary,
  RunsPageSummaryLookups,
  RunsPageSummaryRow,
  RunsPageSummarySection,
  RunsSummaryFocus,
} from "./runsSummaryTypes"

export function buildDetailSummary(
  rows: RunsPageSummaryRow[],
  currentStartTimeMs: number,
  endTimeMs: number,
  previousStartTimeMs: number,
  lookups: RunsPageSummaryLookups,
  focus: RunsSummaryFocus,
  includeActivity = false,
): DetailSummary {
  const currentRows = getRowsForWindow(rows, currentStartTimeMs, endTimeMs)
  const previousRows = getRowsForWindow(
    rows,
    previousStartTimeMs,
    currentStartTimeMs,
  )
  if (currentRows.length === 0) {
    return {
      currentRows,
      detailRowCount: 0,
      leadSentence: null,
      sections: [],
      signalItems: [],
    }
  }

  const rowNoun = getRowNoun(currentRows)
  const hasPriorDetailBaseline = previousRows.length > 0
  const agentCounts = countEntities(
    currentRows,
    (row) => row.agent_id,
    lookups.agentNames,
  )
  const previousAgentCounts = countEntities(
    previousRows,
    (row) => row.agent_id,
    lookups.agentNames,
  )
  const testSetCounts = countEntities(
    currentRows,
    (row) => row.test_set_id,
    lookups.testSetNames,
  )
  const previousTestSetCounts = countEntities(
    previousRows,
    (row) => row.test_set_id,
    lookups.testSetNames,
  )
  const personaCounts = countEntities(
    currentRows,
    (row) => row.persona_id_id,
    lookups.personaNames,
  )
  const previousPersonaCounts = countEntities(
    previousRows,
    (row) => row.persona_id_id,
    lookups.personaNames,
  )
  const metricCounts = countMetrics(currentRows, lookups)
  const previousMetricCounts = countMetrics(previousRows, lookups)
  const coverageDriverItem = hasPriorDetailBaseline
    ? buildCoverageDriverItem({
        agentCounts,
        personaCounts,
        previousAgentCounts,
        previousPersonaCounts,
        previousTestSetCounts,
        rowCount: currentRows.length,
        rowNoun,
        testSetCounts,
      })
    : null

  const metricStats = buildMetricWindowStats(currentRows, previousRows, lookups)
  const evaluationMetricStats = metricStats.filter(
    (metric) => !isOperationalContextMetric(metric),
  )
  const operationalMetricStats = metricStats.filter(isOperationalContextMetric)
  const movementItems = evaluationMetricStats
    .filter(isSignificantMetricMovement)
    .sort((a, b) => {
      const focusDelta =
        Number(metricMatchesFocus(b.id, b.label, focus)) -
        Number(metricMatchesFocus(a.id, a.label, focus))
      const priorityDelta =
        Number(isPriorityMetric(b.label)) - Number(isPriorityMetric(a.label))
      return (
        focusDelta ||
        priorityDelta ||
        getMetricDeltaMagnitude(b) - getMetricDeltaMagnitude(a)
      )
    })
    .map((metric) =>
      buildMetricMovementItem(
        metric,
        buildCoverageComparison(
          getRowsWithMetricValue(currentRows, metric.id),
          getRowsWithMetricValue(previousRows, metric.id),
        ),
        !includeActivity || coverageDriverItem === null,
      ),
    )
    .filter((item): item is string => item !== null)
    .slice(0, MAX_SECTION_ITEMS)

  const operationalMovementItems = operationalMetricStats
    .filter(isSignificantMetricMovement)
    .sort(
      (a, b) =>
        getMetricDeltaMagnitude(b) - getMetricDeltaMagnitude(a) ||
        b.currentCount - a.currentCount,
    )
    .map((metric) =>
      buildMetricMovementItem(
        metric,
        buildCoverageComparison(
          getRowsWithMetricValue(currentRows, metric.id),
          getRowsWithMetricValue(previousRows, metric.id),
        ),
        !includeActivity || coverageDriverItem === null,
      ),
    )
    .filter((item): item is string => item !== null)
    .slice(0, MAX_SECTION_ITEMS)

  const metricConcernItems = [...evaluationMetricStats]
    .sort(
      (a, b) =>
        Number(metricMatchesFocus(b.id, b.label, focus)) -
        Number(metricMatchesFocus(a.id, a.label, focus)),
    )
    .map((metric) => buildMetricConcernItem(metric, rowNoun))
    .filter((item): item is string => item !== null)
    .slice(0, MAX_SECTION_ITEMS)

  const failureClusterItem = buildFailureClusterItem(
    currentRows,
    lookups,
    rowNoun,
  )
  const newAgentItem = hasPriorDetailBaseline
    ? buildNewCoverageItem("Agents", agentCounts, previousAgentCounts)
    : null
  const newTestSetItem = hasPriorDetailBaseline
    ? buildNewCoverageItem("Test sets", testSetCounts, previousTestSetCounts)
    : null
  const newPersonaItem = hasPriorDetailBaseline
    ? buildNewCoverageItem("Personas", personaCounts, previousPersonaCounts)
    : null
  const newMetricItem = hasPriorDetailBaseline
    ? buildNewCoverageItem("Metrics", metricCounts, previousMetricCounts)
    : null
  const topAgents = getTopEntries(agentCounts, MAX_INLINE_LIST_ITEMS)
  const exploratoryDigItems = (
    includeActivity
      ? [
          coverageDriverItem,
          buildCoverageConcentrationDigItem(
            topAgents,
            currentRows.length,
            rowNoun,
          ),
          hasPriorDetailBaseline
            ? buildNewCoverageDigItem(
                "Metrics",
                metricCounts,
                previousMetricCounts,
                "confirm their expected interpretation and thresholds before comparing them to older runs",
              )
            : null,
          hasPriorDetailBaseline
            ? buildNewCoverageDigItem(
                "Test sets",
                testSetCounts,
                previousTestSetCounts,
                "scan early results by test set because aggregate changes may mostly reflect new coverage",
              )
            : null,
          hasPriorDetailBaseline
            ? buildNewCoverageDigItem(
                "Personas",
                personaCounts,
                previousPersonaCounts,
                "compare outcomes within each persona before concluding agent behavior changed",
              )
            : null,
          hasPriorDetailBaseline
            ? buildNewCoverageDigItem(
                "Agents",
                agentCounts,
                previousAgentCounts,
                "review their first runs separately before rolling them into org-level conclusions",
              )
            : null,
        ]
      : []
  ).filter((item): item is string => Boolean(item))
  const failuresDominateLoadedDetail =
    currentRows.filter(isUnsuccessfulRow).length * 2 > currentRows.length
  const worseMovementItem =
    movementItems.find((item) => item.includes("(worse)")) ?? null
  const contentDigItems = [
    ...metricConcernItems,
    worseMovementItem,
    ...exploratoryDigItems,
  ]
    .filter((item): item is string => Boolean(item))
    .filter((item, index, items) => items.indexOf(item) === index)
  const digItems =
    includeActivity && failureClusterItem
      ? failuresDominateLoadedDetail
        ? [failureClusterItem, ...contentDigItems].slice(0, MAX_SECTION_ITEMS)
        : [
            ...contentDigItems.slice(0, MAX_SECTION_ITEMS - 1),
            failureClusterItem,
          ]
      : contentDigItems.slice(0, MAX_SECTION_ITEMS)
  const leadDigItem = contentDigItems[0] ?? null
  const includeCoverageDriverInChanges = Boolean(
    includeActivity &&
    coverageDriverItem &&
    !digItems.includes(coverageDriverItem),
  )
  const changeItems = [
    includeCoverageDriverInChanges ? coverageDriverItem : null,
    ...movementItems,
    ...(includeActivity
      ? [newMetricItem, newTestSetItem, newPersonaItem, newAgentItem]
      : []),
  ].filter((item): item is string => Boolean(item))

  const coverageItems: string[] = []
  if (topAgents.length > 0) {
    coverageItems.push(`Agents used most: ${formatCountEntries(topAgents)}.`)
  }
  const topTestSets = getTopEntries(testSetCounts, MAX_INLINE_LIST_ITEMS)
  if (topTestSets.length > 0) {
    coverageItems.push(
      `Test sets exercised most: ${formatCountEntries(topTestSets)}.`,
    )
  }
  const topPersonas = getTopEntries(personaCounts, MAX_INLINE_LIST_ITEMS)
  if (topPersonas.length > 0) {
    coverageItems.push(
      `Personas covered most: ${formatCountEntries(topPersonas)}.`,
    )
  }
  const topMetrics = getTopEntries(metricCounts, MAX_INLINE_LIST_ITEMS)
  if (topMetrics.length > 0) {
    coverageItems.push(
      `Metrics measured most: ${formatCountEntries(topMetrics)}.`,
    )
  }

  const sections: RunsPageSummarySection[] = []
  const focusItems = buildFocusItems({
    currentRows,
    focus,
    metricStats: evaluationMetricStats,
    previousRows,
    rowNoun,
  })
  if (focusItems.length > 0) {
    sections.push({
      title: FOCUS_SECTION_TITLE,
      items: focusItems,
    })
  }
  if (changeItems.length > 0) {
    sections.push({
      title: WHAT_CHANGED_SECTION_TITLE,
      items: changeItems.slice(0, MAX_SECTION_ITEMS),
    })
  }
  if (operationalMovementItems.length > 0) {
    sections.push({
      title: OPERATIONAL_CONTEXT_SECTION_TITLE,
      items: operationalMovementItems,
    })
  }
  if (digItems.length > 0 || includeActivity) {
    sections.push({
      title: WORTH_DIGGING_SECTION_TITLE,
      items: digItems.length > 0 ? digItems : [NO_OBVIOUS_DIG_ITEM],
    })
  }
  if (includeActivity && coverageItems.length > 0) {
    sections.push({
      title: COVERAGE_SECTION_TITLE,
      items: coverageItems.slice(0, MAX_SECTION_ITEMS),
    })
  }

  const operationalMetricList = formatOperationalMetricList(
    operationalMetricStats,
  )
  const hasEvaluationMetricOutput = evaluationMetricStats.some(
    (metric) => metric.currentCount > 0,
  )
  const observedMetric = [...evaluationMetricStats]
    .filter(
      (metric) => metric.currentCount > 0 && metric.currentAverage !== null,
    )
    .sort((a, b) => b.currentCount - a.currentCount)[0]
  const observedMetricSummary = observedMetric
    ? `${observedMetric.label} averaged ${formatMetricNumber(observedMetric.currentAverage as number)} across ${formatCount(observedMetric.currentCount)} measured ${pluralize(observedMetric.currentCount, rowNoun)}; this alone does not establish overall agent success.`
    : null
  const leadSentence =
    leadDigItem ??
    (includeActivity ? coverageDriverItem : null) ??
    movementItems.find((item) => item.includes("(worse)")) ??
    movementItems[0] ??
    (!hasEvaluationMetricOutput && operationalMetricList
      ? `No agent-evaluation metrics were scored; only operational context is available for ${operationalMetricList}.`
      : null) ??
    (!includeActivity
      ? (observedMetricSummary ??
        (hasEvaluationMetricOutput
          ? "Scored agent-evaluation data is available, but this summary has no supported failure or meaningful metric change to highlight."
          : "Agent performance cannot be assessed: no scored agent-evaluation evidence is available."))
      : topAgents.length > 0
        ? `Available detail is concentrated on ${formatCountEntries(topAgents.slice(0, 2))}.`
        : `Available detail covers ${formatCount(currentRows.length)} loaded ${pluralize(
            currentRows.length,
            rowNoun,
          )}.`)

  return {
    currentRows,
    detailRowCount: currentRows.length,
    leadSentence: `From the available ${rowNoun} detail, ${leadSentence}`,
    sections,
    signalItems: [
      ...digItems,
      ...movementItems,
      ...(includeActivity ? coverageItems : []),
      ...operationalMovementItems,
    ].slice(0, MAX_SIGNAL_ITEMS),
  }
}
